/* Measurement filtering covers every page of Shopify's filtered collection.
 * Native availability, price, brand, department and sorting remain in force. */
(() => {
  'use strict';
  const keys = ['pit', 'length', 'insole'];
  const finite = v => v !== null && v !== '' && Number.isFinite(Number(v));
  function readRanges(params, allowed = keys) {
    const ranges = {};
    for (const key of allowed) {
      const min = params.get(`cf_${key}_min`), max = params.get(`cf_${key}_max`);
      if (finite(min) && finite(max) && Number(min) <= Number(max)) ranges[key] = [Number(min), Number(max)];
    }
    return ranges;
  }
  function matches(product, ranges) {
    return Object.entries(ranges).every(([key, [min, max]]) =>
      product.kind === (key === 'insole' ? 'sneaker' : 'clothing') && finite(product[key]) && Number(product[key]) >= min && Number(product[key]) <= max);
  }
  const strip = params => {
    const out = new URLSearchParams(params);
    for (const key of [...out.keys()]) if (key.startsWith('cf_') || key === 'page' || key === 'section_id') out.delete(key);
    return out;
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {readRanges, matches, strip};
  if (typeof customElements === 'undefined' || customElements.get('cf-measurements')) return;
  customElements.define('cf-measurements', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.cache = new Map(); this.sequence = 0;
      this.fields = [...this.querySelectorAll('[data-measurement]')];
      this.allowed = this.fields.map(f => f.dataset.measurement);
      this.status = this.querySelector('[role="status"]');
      this.hidden = false;
      this.sync();
      this.querySelectorAll('input').forEach(input => input.addEventListener('input', () => {
        const field = input.closest('fieldset'), bound = input.dataset.bound;
        const other = field.querySelector(`input[type="number"][data-bound="${bound === 'min' ? 'max' : 'min'}"]`);
        if (!finite(input.value)) return;
        let value = Math.max(Number(input.min), Math.min(Number(input.max), Number(input.value)));
        value = bound === 'min' ? Math.min(value, Number(other.value)) : Math.max(value, Number(other.value));
        field.querySelectorAll(`[data-bound="${bound}"]`).forEach(el => el.value = value);
        field.dataset.changed = 'true'; this.paint(field);
      }));
      this.querySelector('[data-apply]').addEventListener('click', () => this.apply());
      this.querySelector('[data-reset]').addEventListener('click', () => this.reset());
      document.addEventListener('cf:collection:render', () => { this.sync(); this.refresh(); });
      // facets.js owns browser history and emits the event after restoring it.
      window.CFMeasurements = {
        preserve: (params, event) => {
          const out = new URLSearchParams(params);
          const target = event?.currentTarget;
          const clear = target?.matches?.('.active-facets__button-remove') || (target?.matches?.('.mobile-facets__clear') && target.closest('.mobile-facets__footer'));
          if (!clear) for (const [key, value] of new URLSearchParams(location.search)) if (/^cf_(pit|length|insole)_(min|max)$/.test(key)) out.set(key, value);
          out.delete('cf_page'); return out.toString();
        }
      };
      this.refresh();
    }
    paint(field) {
      const min = field.querySelector('input[type="number"][data-bound="min"]');
      const max = field.querySelector('input[type="number"][data-bound="max"]');
      const total = Number(min.max) - Number(min.min);
      const track = field.querySelector('.cf-measurements__track');
      track.style.setProperty('--start', `${(Number(min.value) - Number(min.min)) / total * 100}%`);
      track.style.setProperty('--end', `${(Number(max.value) - Number(min.min)) / total * 100}%`);
      const lower = field.querySelector('input[type="range"][data-bound="min"]');
      const upper = field.querySelector('input[type="range"][data-bound="max"]');
      lower.setAttribute('aria-valuemax', max.value); upper.setAttribute('aria-valuemin', min.value);
    }
    sync() {
      this.ranges = readRanges(new URLSearchParams(location.search), this.allowed);
      for (const field of this.fields) {
        const range = this.ranges[field.dataset.measurement];
        delete field.dataset.changed;
        for (const input of field.querySelectorAll('input')) input.value = range ? range[input.dataset.bound === 'min' ? 0 : 1] : input.dataset.bound === 'min' ? input.min : input.max;
        this.paint(field);
      }
      if (Object.keys(this.ranges).length) this.querySelector('details').open = true;
    }
    push(params) {
      history.pushState({searchParams:params.toString()}, '', `${location.pathname}?${params}`);
    }
    apply() {
      if ([...this.querySelectorAll('input[type="number"]')].some(el => !el.reportValidity() || !finite(el.value))) return;
      const params = new URLSearchParams(location.search);
      for (const field of this.fields) {
        const key = field.dataset.measurement;
        if (!field.dataset.changed && !this.ranges[key]) continue;
        for (const bound of ['min', 'max']) params.set(`cf_${key}_${bound}`, field.querySelector(`input[type="number"][data-bound="${bound}"]`).value);
      }
      params.delete('page'); params.delete('cf_page');
      this.push(params); this.sync(); this.refresh();
    }
    reset() {
      this.sequence++; this.controller?.abort();
      const params = strip(new URLSearchParams(location.search));
      this.push(params); this.sync(); this.status.textContent = '';
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.renderPage(params.toString(), null, false);
      else location.assign(`${location.pathname}?${params}`);
    }
    async catalog(params, signal) {
      const base = strip(params), cacheKey = base.toString();
      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
      const products = []; let pages = 1;
      for (let page = 1; page <= pages; page++) {
        const query = new URLSearchParams(base); query.set('section_id', 'cf-measurement-catalog'); query.set('page', page);
        const response = await fetch(`${location.pathname}?${query}`, {signal});
        if (!response.ok) throw new Error('Collection request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const el = doc.querySelector('[data-cf-measurement-catalog]');
        if (!el) throw new Error('Missing measurement data');
        const data = JSON.parse(el.textContent);
        if (!Array.isArray(data.products) || data.page !== page || !Number.isInteger(data.pages) || data.pages > 100) throw new Error('Invalid measurement page');
        pages = data.pages; products.push(...data.products);
      }
      // Limit cached full-card catalogs to the most recent filter combinations.
      if (this.cache.size >= 4) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(cacheKey, products); return products;
    }
    async refresh() {
      const sequence = ++this.sequence;
      this.controller?.abort(); this.controller = new AbortController();
      if (!Object.keys(this.ranges).length) { this.status.textContent = ''; const container = document.getElementById('ProductGridContainer'); container.style.visibility = ''; container.removeAttribute('aria-busy'); return; }
      const container = document.getElementById('ProductGridContainer');
      container.setAttribute('aria-busy', 'true');
      // Hide the unfiltered cards while loading, including on a shared URL.
      container.style.visibility = 'hidden';
      this.status.textContent = 'Finding pieces in your measurement range…';
      try {
        const params = new URLSearchParams(location.search);
        const all = await this.catalog(params, this.controller.signal);
        if (sequence !== this.sequence) return;
        const products = all.filter(p => matches(p, this.ranges));
        this.render(products, params);
        const labels = {pit:'Pit to pit', length:'Length', insole:'Insole'};
        this.status.textContent = `${products.length} ${products.length === 1 ? 'piece' : 'pieces'} · ${Object.entries(this.ranges).map(([k,v]) => `${labels[k]} ${v[0]}–${v[1]} cm`).join(' · ')}`;
      } catch (error) {
        if (sequence !== this.sequence || error.name === 'AbortError') return;
        container.style.visibility = 'hidden';
        this.status.textContent = 'Could not load measurements. Try Apply again, or clear measurements to browse all pieces.';
      } finally {
        if (sequence === this.sequence) container.removeAttribute('aria-busy');
      }
    }
    render(products, params) {
      const container = document.getElementById('ProductGridContainer');
      const previousGrid = container.querySelector('#product-grid');
      this.gridClass = previousGrid?.className || this.gridClass || 'grid product-grid grid--2-col-tablet-down grid--4-col-desktop';
      this.sectionId = previousGrid?.dataset.id || this.sectionId || document.querySelector('#main-collection-filters').dataset.id;
      const size = Number(this.dataset.pageSize) || 36;
      const pages = Math.max(1, Math.ceil(products.length / size));
      const page = Math.max(1, Math.min(pages, Math.trunc(Number(params.get('cf_page'))) || 1));
      const collection = document.createElement('div'); collection.className = 'collection page-width';
      const grid = document.createElement('ul'); grid.id = 'product-grid'; grid.dataset.id = this.sectionId; grid.className = this.gridClass;
      for (const product of products.slice((page - 1) * size, page * size)) {
        const li = document.createElement('li'); li.className = 'grid__item'; li.innerHTML = product.card; grid.append(li);
      }
      collection.append(grid);
      if (!products.length) { const empty = document.createElement('p'); empty.className = 'cf-measurements__empty'; empty.textContent = 'No pieces match these measurements. Try a wider range.'; collection.append(empty); }
      if (pages > 1) {
        const nav = document.createElement('nav'); nav.className = 'cf-measurements__pagination'; nav.setAttribute('aria-label', 'Measurement results pages');
        const button = (label, target, disabled) => { const b = document.createElement('button'); b.type = 'button'; b.textContent = label; b.disabled = disabled; b.addEventListener('click', () => {const query = new URLSearchParams(location.search); query.set('cf_page', target); this.push(query); this.refresh(); container.scrollIntoView({block:'start', behavior:'smooth'});}); return b; };
        const count = document.createElement('span'); count.textContent = `${page} / ${pages}`;
        nav.append(button('Previous', page - 1, page === 1), count, button('Next', page + 1, page === pages)); collection.append(nav);
      }
      container.replaceChildren(collection); container.style.visibility = '';
      document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(el => el.textContent = `${products.length} products`);
    }
  });
})();
